import { getDeviceId } from '@/shared/utils/device-id';
import { useTokenStore } from '@/shared/store/token-store';

const ACCOUNT_ID_KEY = 'forced_logout_account_id';

interface ForcedLogoutRequest {
  accountId: number;
  logoutReason: string;
  currentUrl: string | null;
  deviceId: string;
}

/**
 * accountId를 확보하는 순서:
 * 1. Zustand in-memory access token에서 JWT 파싱
 * 2. localStorage fallback (setAccessToken 시 저장해둔 값)
 *
 * 강제 로그아웃 시 Zustand가 비어있는 경우(페이지 새로고침 후 tokenRefresh 실패)
 * localStorage에서 이전 accountId를 가져올 수 있음
 */
const getAccountId = (): number | null => {
  // 1. Zustand에서 직접 추출 시도
  const token = useTokenStore.getState().accessToken;
  if (token) {
    try {
      const parts = token.split('.');
      if (parts[1]) {
        const payload = JSON.parse(atob(parts[1]));
        const id = Number(payload.sub);
        if (id) return id;
      }
    } catch {
      // JWT 파싱 실패 — fallback으로
    }
  }

  // 2. localStorage fallback
  try {
    const stored = localStorage.getItem(ACCOUNT_ID_KEY);
    if (stored) {
      const id = Number(stored);
      if (id) return id;
    }
  } catch {
    // localStorage 접근 불가 — 무시
  }

  return null;
};

/**
 * 비정상 로그아웃 발생 시 BE에 기록 요청
 * navigator.sendBeacon을 사용하여 페이지 이동 중에도 요청이 유실되지 않도록 보장
 */
export const reportForcedLogoutApi = (logoutReason: string): void => {
  try {
    if (typeof window === 'undefined') return;

    const accountId = getAccountId();
    if (!accountId) return;

    const request: ForcedLogoutRequest = {
      accountId,
      logoutReason,
      currentUrl: window.location.href,
      deviceId: getDeviceId(),
    };

    const baseURL = process.env.NEXT_PUBLIC_API_URL || '';
    const blob = new Blob([JSON.stringify(request)], {
      type: 'application/json',
    });
    navigator.sendBeacon(`${baseURL}/v1.0/sign/forced-logout`, blob);
  } catch {
    // 실패해도 무시 - 로그아웃 흐름을 막으면 안 됨
  }
};
